import { useEffect, useRef, useState } from "react";

/**
 * Reveal-on-scroll hook backed by IntersectionObserver. Mirrors the prototype's
 * `revealCheck()` (reveal when an element's top crosses ~92% of the viewport)
 * via a negative bottom rootMargin, then disconnects so the `in` class sticks.
 */
export function useInView<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, inView];
}
